import React from 'react';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import DragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { useTranslation } from '@hooks/index';

interface ListingOption {
    text: string;
    value: string;
}

interface EditReviewPlanStep2Props {
    listingOptions: ListingOption[];
    selectedOption: ListingOption | null;
    onListingSelect: (e: DropDownListChangeEvent) => void;
    watchedColumnItems: DragDropListItemProps[];
    availableColumnItems: DragDropListItemProps[];
    onWatchedColumnsChange: (items: DragDropListItemProps[]) => void;
    onAvailableColumnsChange: (items: DragDropListItemProps[]) => void;
    noDataListingsConfigured: boolean;
}

const EditReviewPlanStep2 = ({
    listingOptions,
    selectedOption,
    onListingSelect,
    watchedColumnItems,
    availableColumnItems,
    onWatchedColumnsChange,
    onAvailableColumnsChange,
    noDataListingsConfigured,
}: EditReviewPlanStep2Props) => {
    const { t } = useTranslation();

    if (noDataListingsConfigured) {
        return (
            <div
                data-testid="no-data-listings-warning"
                className="k-d-flex k-align-items-center k-justify-content-center k-h-full"
            >
                {t('admin:smReviewPlans.editDialog.noDataListings')}
            </div>
        );
    }

    return (
        <div
            className="k-d-flex k-flex-col k-gap-3 k-px-4 k-py-2 k-h-full k-w-full"
            style={{ borderRight: '1px solid #e2e5e9' }}
        >
            <div className="k-d-flex k-align-items-center k-gap-2">
                <label className="k-label" htmlFor="edit-review-plan-listing-select">
                    {t('admin:smReviewPlans.editDialog.dataListing')}
                </label>
                <DropDownList
                    id="edit-review-plan-listing-select"
                    data-testid="edit-review-plan-listing-select"
                    data={listingOptions}
                    textField="text"
                    dataItemKey="value"
                    value={selectedOption}
                    onChange={onListingSelect}
                    style={{ minWidth: 250 }}
                />
            </div>
            <div className="k-flex-1 k-overflow-hidden">
                <DragDropListsCore
                    isTwoLists
                    idField="columnId"
                    list1Header={t('admin:smReviewPlans.editDialog.onWatchColumns')}
                    list2Header={t('admin:smReviewPlans.editDialog.availableColumns')}
                    list1Items={watchedColumnItems}
                    list2Items={availableColumnItems}
                    onChangeItemsLeft={onWatchedColumnsChange}
                    onChangeItemsRight={onAvailableColumnsChange}
                    disabled={!selectedOption}
                    disableReorder
                    renderItemLeft={(item) => (
                        <DragDropItem
                            {...item}
                            toDisplayColumn
                            inactiveIconTooltip={t('admin:smReviewPlans.editDialog.columnNoLongerExists')}
                        />
                    )}
                    renderItemRight={(item) => (
                        <DragDropItem
                            {...item}
                            toDisplayColumn
                            inactiveIconTooltip={t('admin:smReviewPlans.editDialog.columnNoLongerExists')}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default EditReviewPlanStep2;
